import { useState } from 'react';
import { partner } from '../../api/partner';
import type { ReceivedHandoverDetail, TripRow, Vehicle } from '../../api/types';
import { toast } from '../../app/toast';
import { Sheet } from '../../components/Sheet';
import { useT } from '../../i18n';
import { describeError } from '../../lib/errors';
import { useFormat } from '../../lib/format';
import { useAsync } from '../../lib/useAsync';
import { blockingDocs, docNames, localDateInput } from '../../lib/vehicleDocs';

/** Optional: tie the handover to an own trip (existing one without a handover, or a new simple 2-stop trip). */
export function TripLink({ handover, onLinked }: { handover: ReceivedHandoverDetail; onLinked: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn mt-3 w-full" onClick={() => setOpen(true)}>
        {t('ho.linkTrip')}
      </button>
      {open ? (
        <TripLinkSheet
          handover={handover}
          onClose={() => setOpen(false)}
          onLinked={() => {
            setOpen(false);
            onLinked();
          }}
        />
      ) : null}
    </>
  );
}

function TripLinkSheet({ handover, onClose, onLinked }: { handover: ReceivedHandoverDetail; onClose: () => void; onLinked: () => void }) {
  const t = useT();
  const f = useFormat();
  const q = useAsync(async () => {
    const [trips, vehicles] = await Promise.all([partner.trips(), partner.vehicles()]);
    return { trips: trips.filter((x: TripRow) => !x.handover_id && (x.status === 'planned' || x.status === 'in_progress')), vehicles };
  }, []);
  const [busy, setBusy] = useState(false);
  const [vehicle, setVehicle] = useState('');
  const [from, setFrom] = useState(handover.route?.from_place ?? '');
  const [to, setTo] = useState(handover.route?.to_place ?? '');
  // local date, not UTC: in Moldova/Ukraine from 00:00 to 02:00/03:00 the UTC date is still yesterday
  const [date, setDate] = useState(() => localDateInput());

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      toast(t('ho.tripLinked'));
      onLinked();
    } catch (e) {
      toast(t(describeError(e)), 'bad');
    } finally {
      setBusy(false);
    }
  };

  const vehicles: Vehicle[] = q.data?.vehicles ?? [];
  // default: the first vehicle whose mandatory documents are in order (a vehicle without them cannot get a trip)
  const vehicleId = vehicle || (vehicles.find((v) => !blockingDocs(v).length) ?? vehicles[0])?.id || '';
  const chosen = vehicles.find((v) => v.id === vehicleId);
  const chosenBad = blockingDocs(chosen);
  const byId = new Map(vehicles.map((v) => [v.id, v]));
  const create = () =>
    run(() =>
      partner.createTrip({
        vehicle_id: vehicleId,
        handover_id: handover.id,
        stops: [
          { place: from.trim(), country: handover.route?.origin_country ?? undefined, etd_plan: `${date}T08:00:00Z` },
          { place: to.trim(), country: handover.route?.dest_country ?? undefined },
        ],
      }),
    );

  return (
    <Sheet title={t('ho.linkTrip')} onClose={onClose}>
      {q.loading && !q.data ? <p className="text-lab">{t('app.loading')}</p> : null}
      {q.error ? <p className="text-bad">{t(describeError(q.error))}</p> : null}
      {q.data ? (
        <>
          <h3 className="label">{t('ho.pickTrip')}</h3>
          {q.data.trips.length ? (
            <ul className="space-y-2">
              {q.data.trips.map((tr) => {
                const bad = blockingDocs(byId.get(tr.vehicle_id));
                return (
                  <li key={tr.id}>
                    <button type="button" className="btn w-full justify-start text-left" disabled={busy} onClick={() => void run(() => partner.linkTrip(tr.id, handover.id))}>
                      <span className="min-w-0 flex-1 truncate">
                        {tr.number ?? '—'} · {tr.route} {tr.starts_at ? `· ${f.date(tr.starts_at, false)}` : ''}
                      </span>
                    </button>
                    {bad.length ? <p className="mt-1 text-[14px] text-bad">{t('trips.departureBlocked', { docs: docNames(bad, f.locale) })}</p> : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-[15px] text-lab">{t('ho.noFreeTrips')}</p>
          )}
          {vehicles.length ? (
            <form
              className="mt-5 space-y-3 border-t border-line pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (from.trim() && to.trim() && vehicleId && !chosenBad.length) void create();
              }}
            >
              <h3 className="label">{t('ho.createTrip')}</h3>
              <div>
                <label htmlFor="tl-v" className="label">
                  {t('ho.tripVehicle')}
                </label>
                <select
                  id="tl-v"
                  className="input"
                  value={vehicleId}
                  onChange={(e) => setVehicle(e.target.value)}
                  aria-describedby={chosenBad.length ? 'tl-v-docs' : undefined}
                >
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.plate} {v.model ? `· ${v.model}` : ''}
                      {blockingDocs(v).length ? ` — ${t('ho.vehicleNoDocsShort')}` : ''}
                    </option>
                  ))}
                </select>
                {chosenBad.length ? (
                  <p id="tl-v-docs" role="alert" className="mt-1 text-[14px] text-bad">
                    {t('ho.vehicleNoDocs', { docs: docNames(chosenBad, f.locale) })}
                  </p>
                ) : null}
              </div>
              <div>
                <label htmlFor="tl-from" className="label">
                  {t('ho.tripFrom')}
                </label>
                <input id="tl-from" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <label htmlFor="tl-to" className="label">
                  {t('ho.tripTo')}
                </label>
                <input id="tl-to" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <div>
                <label htmlFor="tl-date" className="label">
                  {t('ho.tripDate')}
                </label>
                <input id="tl-date" type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <button type="submit" className="btn-primary min-h-big w-full" disabled={busy || !from.trim() || !to.trim() || !vehicleId || chosenBad.length > 0}>
                {t('ho.createTrip')}
              </button>
            </form>
          ) : null}
        </>
      ) : null}
    </Sheet>
  );
}
